import { defineStore } from "pinia";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import { computed, ref } from "vue";
import { MatePersona } from "@/types/chat";
import {
  createOneMatePersona,
  deleteOneMatePersona,
  readMatePersonaPage,
  updateOneMatePersona,
} from "@/apis/chat";

const PER_PAGE = 999;

export const useMatePersona = defineStore("matePersona", () => {
  const entityStore = entityStoreFactory<MatePersona>("id");
  const { updateMap, getEntity } = entityStore;
  const mateId2MatePersonaIds = ref<Record<number, number[]>>({});
  const mateId2MatePersonaList = computed(() => {
    const res: Record<number, MatePersona[]> = {};
    for (const mateId in mateId2MatePersonaIds.value) {
      res[mateId] = mateId2MatePersonaIds.value[mateId].map(
        (id) => getEntity(id)!
      );
    }
    return res;
  });
  const genericMatePersonaIds = ref<number[]>([]);
  const genericMatePersonaList = computed(() =>
    genericMatePersonaIds.value.map((id) => getEntity(id)!)
  );

  const getMatePersonaList = (mateId: number) =>
    mateId2MatePersonaList.value[mateId] ?? [];

  // Actions
  const fetchMatePersonaList = async (mateId?: number) => {
    const { page } = await readMatePersonaPage({
      filter: { mateId },
      pagination: { page: 1, perPage: PER_PAGE },
      sort: { field: "dtUpdate", order: "desc" },
    });
    const matePersonas = updateMap(page.data);
    if (mateId) {
      mateId2MatePersonaIds.value[mateId] = matePersonas.map(
        (item) => item.id!
      );
    } else {
      const presetIds = matePersonas
        .filter((item) => item.isPreset === 1)
        .map((item) => item.id!);
      const otherIds = matePersonas
        .filter((item) => item.isPreset === 0)
        .map((item) => item.id!);
      genericMatePersonaIds.value = [...presetIds, ...otherIds];
    }
  };

  const getOrFetchMatePersonaList = async (mateId?: number, force = false) => {
    if (mateId) {
      if (!force && getMatePersonaList(mateId).length > 0) {
        return mateId2MatePersonaList.value;
      } else {
        return await fetchMatePersonaList(mateId);
      }
    } else {
      if (!force && genericMatePersonaList.value.length > 0) {
        return genericMatePersonaList.value;
      } else {
        return await fetchMatePersonaList();
      }
    }
  };

  const createMatePersona = async (
    params: Parameters<typeof createOneMatePersona>[0],
    media: Media
  ) => {
    const res = await createOneMatePersona(params);
    const matePersona = res.item;
    matePersona.media = media;
    updateMap([matePersona]);
    mateId2MatePersonaIds.value[matePersona.mateId].unshift(matePersona.id!);
  };

  const deleteMatePersona = async (id: number) => {
    await deleteOneMatePersona(id);
    const matePersona = getEntity(id)!;
    mateId2MatePersonaIds.value[matePersona.mateId] =
      mateId2MatePersonaIds.value[matePersona.mateId].filter(
        (item) => item !== id
      );
  };

  const updateMatePersona = async (
    id: number,
    params: Parameters<typeof updateOneMatePersona>[1],
    media: Media
  ) => {
    const res = await updateOneMatePersona(id, params);
    const matePersona = res.item;
    matePersona.media = media;
    updateMap([matePersona]);
    return matePersona;
  };

  const disableCache = () => {
    mateId2MatePersonaIds.value = {};
  };

  return {
    // state
    mateId2MatePersonaList,
    genericMatePersonaList,
    // actions
    createMatePersona,
    deleteMatePersona,
    updateMatePersona,
    disableCache,
    getMatePersonaList,
    getOrFetchMatePersonaList,
  };
});
